import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, BehaviorSubject } from 'rxjs'
import { first, map } from 'rxjs/operators'

import { environment } from 'environments/environment'

import { Mapper } from './mapper.service'

export interface ApiResponse<T> {
  metadata?: {
    trace_id: string
    current_user: any
    pagination: any
  }
  error?: {
    status: number
    messages: string[]
  }
  result: T
  [index: string]: any
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(private http: HttpClient) { }

  public get(path: string, options?: any, includesRoot: boolean = false): Observable<any> {
    return this.http.get(this.uri(path, includesRoot), this.getOptions(options))
  }

  public post(path: string, body: any, options?: any, includesRoot: boolean = false): Observable<any> {
    return this.http.post(this.uri(path, includesRoot), body, this.getOptions(options))
  }

  public put(path: string, body: any, options?: any, includesRoot: boolean = false): Observable<any> {
    return this.http.put(this.uri(path, includesRoot), body, this.getOptions(options))
  }

  public delete(path: string, options?: any, includesRoot: boolean = false): Observable<any> {
    return this.http.delete(this.uri(path, includesRoot), this.getOptions(options))
  }

  public findCurrent<T>(url: string, subject: BehaviorSubject<T>, klass: new(...args: any[]) => T): void {
    this.get(url)
      .subscribe(
        (data: any) => {
          const model = Mapper.as<T>(data.result, klass)
          model['metadata'] = data.metadata
          subject.next(model)
        }
      )
  }

  public findAs<T>(url: string, klass: new(...args: any[]) => T): Observable<T> {
    return this.get(url)
      .pipe(first())
      .pipe(map(data => {
        const r = Mapper.as<T>(data.result, klass)
        r['metadata'] = data.metadata
        return r
    }))
  }

  public findAllAs<T>(url: string, params: any, klass: new(...args: any[]) => T): Observable<T[]> {
    return this.get(url, { params })
      .pipe(first())
      .pipe(map(data => {
        const r = Mapper.asArray<T>(data.result, klass)
        r['metadata'] = data.metadata
        return r
      }))
  }

  public uri(path: string, includesRoot: boolean = false): string {
    if (includesRoot) {
      return encodeURI(path)
    }

    return this.apiUrl() + encodeURI(path)
  }

  private getOptions(options?: any): any {
    options = options || { }
    options.withCredentials = true
    options.headers = options.headers || { }
    options.headers.Accept = options.headers.Accept || 'application/json'
    return options
  }

  private apiUrl(): string {
    const indexedOrigin = window.location.origin.split('.')
    const indexedApi = environment.apiUrl.split('.')

    if (indexedOrigin.length > 1 && indexedApi.length > 1 && indexedOrigin[1] === indexedApi[1]) {
      return environment.apiUrl
    }
    return environment.fallbackApiUrl
  }
}
